// app/javascript/controllers/dhtmlx_scheduler_controller.js
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { schedulerMenuId: Number, startedAt: String };
  static targets = ["popup", "startedAtInput"];

  connect() {
    this.scheduler = window.scheduler;
    this.initScheduler();
  }

  disconnect() {
    this.scheduler.destructor?.();
  }

  async initScheduler() {
    const scheduler = this.scheduler;

    scheduler.plugins({ timeline: true });
    scheduler.i18n.setLocale("jp");
    scheduler.config.details_on_create = false;
    scheduler.config.details_on_dblclick = false;
    scheduler.config.drag_resize = false;

    scheduler.locale.labels.section_time = "日付";
    scheduler.locale.labels.section_select = "対象作業";

    const response = await fetch(
      `/scheduler_menus/${this.schedulerMenuIdValue}`,
      {
        headers: { Accept: "application/json" }
      }
    );
    const data = await response.json();

    const machines = data.map((m) => ({ key: m.id, label: m.name }));
    const factoryType = this.popupTarget.getAttribute("name");
    const large_works = data
      .flatMap((machine) => machine.works)
      .map((large_work) => ({
        start_date: large_work.started_at,
        end_date: new Date(large_work.ended_at),
        machine_id: large_work.machine_id,
        department_id: large_work.order_department_id,
        procedure_name: large_work.procedure_name,
        text: this.containerText(factoryType, large_work),
        large_work_id: large_work.id,
        charge_full_flag: large_work.charge_full_flag,
        status: large_work.status,
        order_status: large_work.order_status
      }));

    scheduler.config.time_step = 240;

    scheduler.createTimelineView({
      name: "timeline",
      x_unit: "hour",
      x_date: " ",
      x_step: 4,
      x_size: 48,
      column_width: 90,
      scrollable: true,
      section_autoheight: true,
      y_unit: machines,
      y_property: "machine_id",
      dy: 48,
      event_min_dy: 64,
      render: "bar",
      second_scale: {
        x_unit: "day",
        x_date: "%F %d日(%D)"
      }
    });

    scheduler.attachEvent("onEventSave", (id, e) => {
      e.id = id;
      this.handleWorkEvent(e);
      return true;
    });

    scheduler.attachEvent("onEventChanged", (_id, e) => {
      this.handleWorkEvent(e);
      return true;
    });

    scheduler.attachEvent("onBeforeEventDelete", (_id, e) => {
      const id = e.large_work_id;
      if (!id) return true;
      fetch(`/works/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        }
      });
      return true;
    });

    scheduler.templates.event_class = (_start, _end, event) =>
      this.eventClass(event);

    scheduler.config.default_date = "%Y年%m月%d日";

    scheduler.init(
      this.element.querySelector("#scheduler"),
      this.startedAtValue || new Date(),
      "timeline"
    );
    scheduler.attachEvent("onBeforeLightbox", () => false);

    scheduler.parse(large_works);
    this.highlightMidnight();

    document.addEventListener("mousemove", this.enlargeScaleHeader);
  }

  async handleWorkEvent(e) {
    const id = e.large_work_id;
    if (!id) return true;

    const machine_id = e.machine_id;
    const started_at = e.start_date;
    const ended_at = e.end_date;
    const status = e.status;

    const response = await fetch(`/works/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({
        work: { machine_id, started_at, ended_at, status }
      })
    });
    const work = await response.json();
    if (work.status == "success") return;

    const text = `${work.status}</br>${work.client_abbr}</br>${work.order_code}</br>${work.procedure_name}</br>${work.order_in_delivery_arrival_at} - ${work.order_out_delivery_at}`;

    this.scheduler.getEvent(e.id).text = text;
    this.scheduler.updateEvent(e.id);

    return true;
  }

  containerText(factoryType, large_work) {
    if (factoryType === "die") {
      return `
        <div name="large_work_id" data-large-work-id="${large_work.id}">${
        large_work.order_memo ? "※" : ""
      }${large_work.out_delivery_destination_company_abbr_cache}</div>
        <div>${large_work.order_car_type}</div>
        <div>${
          large_work.order_product_code || large_work.order_product_name
        }</div>
        <div>${large_work.order_mold_name}</div>
        <div>${large_work.loading_and_unloading_abbr_cache}</div>
      `;
    } else {
      return `
        <div name="large_work_id" data-large-work-id="${large_work.id}">${
        large_work.order_memo ? "※" : ""
      }${large_work.order_code}</div>
        <div>${large_work.branch_abbr_cache}</div>
        <div>${large_work.order_product_name}</div>
        <div>${large_work.order_total_quantity}個・${
        large_work.max_plating_thickness
      }μ</div>
        <div>${large_work.order_deadline_at}</div>
      `;
    }
  }

  eventClass(event) {
    let borderClass = "border border-secondary";
    if (event.order_status === "取消") {
      borderClass = "border border-black";
    } else if (event.status === "完了" || event.status === "検査完了") {
      borderClass = "border border-blue";
    } else if (event.status === "めっき完了") {
      borderClass = "border border-2 border-green";
    } else if (event.charge_full_flag) {
      borderClass = "border border-danger";
    } else if (event.status === "承認済み") {
      borderClass = "border border-black";
    }

    let backgroundClass = "bg-white";
    if (event.department_id === 3) {
      backgroundClass = "bg-lightgreen";
    } else if (event.procedure_name === "TKコート") {
      backgroundClass = "bg-lightblue";
    } else if (event.procedure_name === "TUPめっき") {
      backgroundClass = "bg-lightyellow";
    } else if (event.procedure_name === "追加めっき") {
      backgroundClass = "bg-lightorange";
    } else if (event.procedure_name === "TESS") {
      backgroundClass = "bg-lightgrey";
    }

    let textClass = "text-black";
    if (event.order_status === "取消") {
      textClass = "text-black text-decoration-line-through";
    } else if (event.procedure_name === "剥離") {
      textClass = "text-danger";
    } else if (
      event.procedure_name === "再めっき" &&
      event.status === "計画未承認"
    ) {
      textClass = "text-primary-opacity";
    } else if (event.procedure_name === "再めっき") {
      textClass = "text-blue";
    } else if (event.status === "計画未承認") {
      textClass = "text-secondary";
    }

    return `${borderClass} ${backgroundClass} ${textClass} no-wrap`;
  }

  highlightMidnight() {
    document.querySelectorAll(".dhx_matrix_cell").forEach((element) => {
      if (!element.dataset.colDate?.includes("00:00")) return;
      element.style.borderLeft = "3px solid #000";
    });
  }

  enlargeScaleHeader() {
    document
      .querySelectorAll(".dhx_timeline_scale_header")
      .forEach((el) => (el.style.height = "40px"));
  }

  navigateToStartedAt(event) {
    event.preventDefault();
    const started_at = this.startedAtInputTarget.value;
    this.scheduler.setCurrentView(new Date(started_at), "timeline");
    this.highlightMidnight();
  }

  yesterday() {
    const current = new Date(this.startedAtInputTarget.value);
    current.setDate(current.getDate() - 1);
    this.startedAtInputTarget.value = current.toISOString().split("T")[0];
    this.scheduler.setCurrentView(current, "timeline");
    this.highlightMidnight();
  }

  tomorrow() {
    const current = new Date(this.startedAtInputTarget.value);
    current.setDate(current.getDate() + 1);
    this.startedAtInputTarget.value = current.toISOString().split("T")[0];
    this.scheduler.setCurrentView(current, "timeline");
    this.highlightMidnight();
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  async updateWorkStatusApproved(event) {
    const eventId = event.target.closest(".row").dataset.eventId;
    const schedulerEvent = this.scheduler.getEvent(eventId);
    const text = event.target.innerText;
    if (text === "承認") {
      if (!confirm("この作業を承認しますか？")) return;
      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      await fetch(`/works/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ work: { status: "承認済み" } })
      });

      event.target.innerText = "承認済み";
      event.target.classList.remove("btn-primary");
      event.target.classList.add("btn-secondary");
      schedulerEvent.status = "承認済み";
      this.scheduler.updateEvent(eventId);
    } else {
      if (!confirm("この作業の承認を取り消しますか？")) return;
      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      await fetch(`/works/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          work: { status: "計画未承認", color: "#cccccc" }
        })
      });

      event.target.innerText = "承認";
      event.target.classList.remove("btn-secondary");
      event.target.classList.add("btn-primary");
      schedulerEvent.status = "計画未承認";
      this.scheduler.updateEvent(eventId);
    }
  }

  async updateWorkChargeFull(event) {
    const eventId = event.target.closest(".row").dataset.eventId;
    const schedulerEvent = this.scheduler.getEvent(eventId);
    const text = event.target.innerText;
    if (event.target.classList.contains("btn-secondary")) {
      if (!confirm("この時間帯の予定一杯を解除しますか？")) return;
      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      await fetch(`/works/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ work: { charge_full_flag: false } })
      });

      event.target.classList.remove("btn-secondary");
      event.target.classList.add("btn-primary");
      schedulerEvent.charge_full_flag = false;
      this.scheduler.updateEvent(eventId);
      $(`[data-event-id=${eventId}]`).removeClass("border-danger");
    } else {
      if (!confirm("この時間帯の予定一杯にしますか？")) return;
      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      await fetch(`/works/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ work: { charge_full_flag: true } })
      });

      event.target.classList.remove("btn-primary");
      event.target.classList.add("btn-secondary");
      schedulerEvent.charge_full_flag = true;
      this.scheduler.updateEvent(eventId);
      $(`[data-event-id=${eventId}]`).addClass("border-danger");
    }
  }
  async updateWorkConfirmedByGenba(event) {
    const eventId = event.target.closest(".row").dataset.eventId;
    const schedulerEvent = this.scheduler.getEvent(eventId);
    if (event.target.classList.contains("btn-secondary")) {
      if (!confirm("この作業の現場確認を取り消しますか？")) return;
      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      await fetch(`/works/${id}/update_orders_confirmed_by_genba`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ work: { confirmed_by_genba: false } })
      });

      event.target.classList.remove("btn-secondary");
      event.target.classList.add("btn-primary");
      schedulerEvent.confirmed_by_genba = false;
      this.scheduler.updateEvent(eventId);
    } else {
      if (
        this.popupTarget.textContent.includes("面積: null") ||
        this.popupTarget.textContent.includes("重量: null")
      ) {
        alert("面積または重量が空欄です");
        return;
      }

      if (!confirm("この作業を現場確認済みにしますか？")) return;
      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      await fetch(`/works/${id}/update_orders_confirmed_by_genba`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ work: { confirmed_by_genba: true } })
      });

      event.target.classList.remove("btn-primary");
      event.target.classList.add("btn-secondary");
      schedulerEvent.confirmed_by_genba = true;
      this.scheduler.updateEvent(eventId);
    }
  }

  async updateWorkStatusFinished(event) {
    const $row = event.target.closest(".row");
    const eventId = $row.dataset.eventId;
    const workId = $row.dataset.workId;
    if (!workId) return;

    const text = event.target.innerText;
    const borderClass = text === "めっき完了" ? "border-green" : "border-blue";
    if (!confirm(`この作業を${text}にしますか？`)) return;

    await fetch(`/works/${workId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ work: { status: text } })
    });

    event.target.classList.remove("btn-primary");
    event.target.classList.add("btn-secondary");

    const schedulerEvent = this.scheduler.getEvent(eventId);
    schedulerEvent.status = text;
    this.scheduler.updateEvent(eventId);

    const eventEl = document.querySelector(`[data-event-id="${eventId}"]`);
    if (eventEl) {
      eventEl.classList.remove("border-blue", "border-green");
      eventEl.classList.add(borderClass);
    }
  }

  async createEnergizeRecord(event) {
    const id = event.target.closest(".row").dataset.workId;
    if (!id) return;

    const response = await fetch(`/works/create_energize_record`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ work_ids: [id] })
    });
    const data = await response.json();
    window.location.href = `/energize_records/${data.energize_record_id}/edit`;
  }

  async createInspectionReport(event) {
    if (event.target.classList.contains("btn-primary")) {
      if (!confirm("本作業の検査表を作成しますか？")) return;

      const id = event.target.closest(".row").dataset.workId;
      if (!id) return;

      window.location.href = `/inspection_reports/new?inspection_report[work_id]=${id}`;
    } else {
      const id = event.target.dataset.inspectionReportId;
      window.location.href = `/inspection_reports/${id}/edit`;
    }
  }
}
